import { arrayOf, bool, func, instanceOf, number, object } from 'prop-types';
import { useIntl } from 'react-intl';

import { ReactComponent as BudgeIcon } from 'assets/icons/budge.svg';
import { ReactComponent as FolderIcon } from 'assets/icons/folder-icon.svg';
import Checkbox from 'components/common/Checkbox';
import EmptyState from 'components/common/EmptyState';
import CenteredLoading from 'components/common/Loading/CenteredLoading';
import { forecastingProjectShape } from 'constants/shapes';
import { getNextMonths, dateToString } from 'utils/date';

import ProjectItem from './ProjectItem';
import { Container, Header, Column } from './styles';

const width = '30rem';

function ProjectList({
  forecastingProjects,
  isLoading,
  countMonths,
  showToggle,
  includedIds,
  onToggle,
  onSetIncludedIds,
  winProbabilities,
  onProbabilityChange
}) {
  const intl = useIntl();
  const listMonth = getNextMonths(countMonths);

  const allVisibleSelected =
    forecastingProjects?.length > 0 && forecastingProjects.every(p => includedIds?.has(p.id));

  const handleToggleAllVisible = () => {
    const visibleIds = new Set(forecastingProjects?.map(p => p.id) || []);
    const next = new Set(includedIds);
    if (allVisibleSelected) {
      visibleIds.forEach(id => next.delete(id));
    } else {
      visibleIds.forEach(id => next.add(id));
    }
    onSetIncludedIds?.(next);
  };

  return (
    <Container>
      <Header>
        {showToggle && (
          <Column minWidth="4rem" maxWidth="4rem">
            <Checkbox value={allVisibleSelected} onChange={handleToggleAllVisible} variant="new" />
          </Column>
        )}
        <Column minWidth={width} textAlign="start">
          <FolderIcon />
          {intl.formatMessage({ id: 'common.project' })}
        </Column>
        {!showToggle && (
          <>
            <Column maxWidth={width} />
            <Column maxWidth={width} textAlign="start">
              {intl.formatMessage({ id: 'forecasting.mainPage.projectList.currentYear' })}
              <BudgeIcon />
            </Column>
          </>
        )}
        {showToggle && (
          <Column minWidth="8rem" maxWidth="8rem">
            {intl.formatMessage({ id: 'forecasting.mainPage.winProbability' })}
          </Column>
        )}
        {listMonth.map((item, index) => (
          <Column key={index} maxWidth={width}>
            {dateToString(item.date, 'MMM')}
          </Column>
        ))}
      </Header>
      {isLoading && <CenteredLoading />}
      {forecastingProjects?.length > 0 &&
        forecastingProjects?.map(project => (
          <ProjectItem
            key={project.id}
            project={project}
            width={width}
            listMonth={listMonth}
            showToggle={showToggle}
            isIncluded={includedIds?.has(project.id)}
            onToggle={onToggle}
            winProbability={winProbabilities?.[project.id]}
            onProbabilityChange={onProbabilityChange}
          />
        ))}
      {!forecastingProjects?.length && !isLoading && (
        <EmptyState textIntlId="financial.closePeriod.emptyState" />
      )}
    </Container>
  );
}

ProjectList.propTypes = {
  forecastingProjects: arrayOf(forecastingProjectShape),
  isLoading: bool,
  countMonths: number,
  showToggle: bool,
  includedIds: instanceOf(Set),
  onToggle: func,
  onSetIncludedIds: func,
  winProbabilities: object,
  onProbabilityChange: func
};

export default ProjectList;
